import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { API } from "../../App";
import { 
  Gift, Plus, Edit2, Trash2, Copy, QrCode, 
  Search, Download, Layers, ExternalLink 
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogTitle, DialogHeader } from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

export default function AdminArtifacts() {
  const [artifacts, setArtifacts] = useState([]);
  const [weddings, setWeddings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedWeddingFilter, setSelectedWeddingFilter] = useState("all");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedArtifact, setSelectedArtifact] = useState(null);
  const [formData, setFormData] = useState({
    wedding_id: "",
    artifact_type: "giveaway",
    label: "",
    destination_type: "landing_page",
    destination_url: "",
  });
  const [batchData, setBatchData] = useState({
    wedding_id: "",
    artifact_type: "giveaway",
    batch_label: "",
    count: 10,
    destination_type: "landing_page",
    destination_url: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const [artifactsRes, weddingsRes] = await Promise.all([
        axios.get(`${API}/artifacts`, { headers }),
        axios.get(`${API}/weddings`, { headers }),
      ]);
      setArtifacts(artifactsRes.data);
      setWeddings(weddingsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/artifacts`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArtifacts([...artifacts, response.data]);
      setCreateModalOpen(false);
      toast.success("Artifact created successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create artifact");
    }
  };

  const handleBatchCreate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/artifacts/batch`, batchData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArtifacts([...artifacts, ...response.data]);
      setBatchModalOpen(false);
      toast.success(`Created ${response.data.length} artifacts!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create batch");
    }
  };

  const handleDelete = async (artifactId) => {
    if (!window.confirm("Delete this artifact?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/artifacts/${artifactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArtifacts(artifacts.filter(a => a.artifact_id !== artifactId));
      toast.success("Artifact deleted");
    } catch (error) {
      toast.error("Failed to delete artifact");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const getTokenUrl = (token) => {
    return `${window.location.origin}/t/${token}`;
  };

  const getWeddingName = (weddingId) => {
    const wedding = weddings.find(w => w.wedding_id === weddingId);
    return wedding ? `${wedding.couple_name_1} & ${wedding.couple_name_2}` : "Unknown";
  };

  const filteredArtifacts = artifacts.filter(a => {
    const matchesSearch = 
      a.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.token.toLowerCase().includes(searchTerm.toLowerCase()) ||
      a.artifact_type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesWedding = selectedWeddingFilter === "all" || a.wedding_id === selectedWeddingFilter;
    return matchesSearch && matchesWedding;
  });

  const artifactTypes = [
    { value: "wedding_orb", label: "Wedding Orb" },
    { value: "giveaway", label: "Giveaway" },
    { value: "keychain", label: "Keychain" },
    { value: "card", label: "Card" },
    { value: "other", label: "Other" },
  ];

  const destinationTypes = [
    { value: "landing_page", label: "Landing Page" },
    { value: "tree_page", label: "Wedding Tree" },
    { value: "gratitude_page", label: "Gratitude Page" },
    { value: "sde_page", label: "SDE Page" },
    { value: "custom_url", label: "Custom URL" },
  ];

  return (
    <div className="space-y-8" data-testid="admin-artifacts">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-wedding-ivory">Artifacts</h1>
          <p className="text-wedding-ivory/60 mt-1">Manage wedding tokens and giveaways</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={() => {
              setFormData({ wedding_id: "", artifact_type: "giveaway", label: "", destination_type: "landing_page", destination_url: "" });
              setCreateModalOpen(true);
            }}
            className="btn-secondary"
          >
            <Plus className="w-4 h-4 mr-2" />
            Single
          </Button>
          <Button 
            onClick={() => {
              setBatchData({ wedding_id: "", artifact_type: "giveaway", batch_label: "", count: 10, destination_type: "landing_page", destination_url: "" });
              setBatchModalOpen(true);
            }}
            className="btn-primary"
            data-testid="create-batch-btn"
          >
            <Layers className="w-4 h-4 mr-2" />
            Create Batch
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-wedding-ivory/40" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by token, label, or type..."
            className="pl-10 bg-wedding-charcoal border-white/10"
          />
        </div>
        <Select value={selectedWeddingFilter} onValueChange={setSelectedWeddingFilter}>
          <SelectTrigger className="w-[250px] bg-wedding-charcoal border-white/10">
            <SelectValue placeholder="Filter by wedding" />
          </SelectTrigger>
          <SelectContent className="bg-wedding-charcoal border-white/10">
            <SelectItem value="all">All Weddings</SelectItem>
            {weddings.map(wedding => (
              <SelectItem key={wedding.wedding_id} value={wedding.wedding_id}>
                {wedding.couple_name_1} & {wedding.couple_name_2}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total", value: artifacts.length },
          { label: "Active", value: artifacts.filter(a => a.status === "active").length },
          { label: "Wedding Orbs", value: artifacts.filter(a => a.artifact_type === "wedding_orb").length },
          { label: "Giveaways", value: artifacts.filter(a => a.artifact_type === "giveaway").length },
        ].map((stat) => (
          <div key={stat.label} className="stat-card">
            <div className="stat-value">{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Artifacts Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredArtifacts.length === 0 ? (
        <div className="text-center py-12">
          <Gift className="w-16 h-16 text-wedding-gold/30 mx-auto mb-4" />
          <p className="text-wedding-ivory/60">No artifacts found</p>
        </div>
      ) : (
        <div className="card-hover overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Token</th>
                  <th>Label</th>
                  <th>Wedding</th>
                  <th>Type</th>
                  <th>Destination</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredArtifacts.map((artifact) => (
                  <tr key={artifact.artifact_id}>
                    <td>
                      <code className="text-wedding-gold text-sm font-mono bg-wedding-subtle px-2 py-1 rounded">
                        {artifact.token}
                      </code>
                    </td>
                    <td className="text-wedding-ivory">{artifact.label || "—"}</td>
                    <td className="text-wedding-ivory/70 text-sm">
                      {getWeddingName(artifact.wedding_id)}
                    </td>
                    <td className="text-wedding-ivory/70 text-sm capitalize">
                      {artifact.artifact_type?.replace("_", " ")}
                    </td>
                    <td className="text-wedding-ivory/70 text-sm capitalize">
                      {artifact.destination_type?.replace("_", " ")}
                    </td>
                    <td>
                      <span className={`badge ${artifact.status === "active" ? "badge-active" : "badge-inactive"}`}>
                        {artifact.status}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => {
                            setSelectedArtifact(artifact);
                            setQrModalOpen(true);
                          }}
                          className="p-2 text-wedding-ivory/50 hover:text-wedding-gold transition-colors"
                          title="QR Code"
                        >
                          <QrCode className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => copyToClipboard(getTokenUrl(artifact.token))}
                          className="p-2 text-wedding-ivory/50 hover:text-wedding-gold transition-colors"
                          title="Copy URL"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(artifact.artifact_id)}
                          className="p-2 text-wedding-ivory/50 hover:text-red-500 transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Single Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="bg-wedding-charcoal border-white/10">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-wedding-ivory">Create Artifact</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-6 mt-4">
            <div>
              <Label className="form-label">Wedding</Label>
              <Select value={formData.wedding_id} onValueChange={(v) => setFormData({ ...formData, wedding_id: v })}>
                <SelectTrigger className="w-full bg-wedding-subtle border-white/10 mt-2">
                  <SelectValue placeholder="Select wedding" />
                </SelectTrigger>
                <SelectContent className="bg-wedding-charcoal border-white/10">
                  {weddings.map(wedding => (
                    <SelectItem key={wedding.wedding_id} value={wedding.wedding_id}>
                      {wedding.couple_name_1} & {wedding.couple_name_2}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="form-label">Artifact Type</Label>
              <Select value={formData.artifact_type} onValueChange={(v) => setFormData({ ...formData, artifact_type: v })}>
                <SelectTrigger className="w-full bg-wedding-subtle border-white/10 mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wedding-charcoal border-white/10">
                  {artifactTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="form-label">Label</Label>
              <Input
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="Wedding Orb #1"
                className="input-premium mt-2"
              />
            </div>
            <div>
              <Label className="form-label">Destination Type</Label>
              <Select value={formData.destination_type} onValueChange={(v) => setFormData({ ...formData, destination_type: v })}>
                <SelectTrigger className="w-full bg-wedding-subtle border-white/10 mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wedding-charcoal border-white/10">
                  {destinationTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {formData.destination_type === "custom_url" && (
              <div>
                <Label className="form-label">Custom URL</Label>
                <Input
                  value={formData.destination_url}
                  onChange={(e) => setFormData({ ...formData, destination_url: e.target.value })}
                  placeholder="https://..."
                  className="input-premium mt-2"
                />
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button type="button" onClick={() => setCreateModalOpen(false)} className="btn-secondary">
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                Create
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Batch Create Modal */}
      <Dialog open={batchModalOpen} onOpenChange={setBatchModalOpen}>
        <DialogContent className="bg-wedding-charcoal border-white/10">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-wedding-ivory">Create Artifact Batch</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleBatchCreate} className="space-y-6 mt-4">
            <div>
              <Label className="form-label">Wedding</Label>
              <Select value={batchData.wedding_id} onValueChange={(v) => setBatchData({ ...batchData, wedding_id: v })}>
                <SelectTrigger className="w-full bg-wedding-subtle border-white/10 mt-2">
                  <SelectValue placeholder="Select wedding" />
                </SelectTrigger>
                <SelectContent className="bg-wedding-charcoal border-white/10">
                  {weddings.map(wedding => (
                    <SelectItem key={wedding.wedding_id} value={wedding.wedding_id}>
                      {wedding.couple_name_1} & {wedding.couple_name_2}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="form-label">Type</Label>
                <Select value={batchData.artifact_type} onValueChange={(v) => setBatchData({ ...batchData, artifact_type: v })}>
                  <SelectTrigger className="w-full bg-wedding-subtle border-white/10 mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wedding-charcoal border-white/10">
                    {artifactTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="form-label">Count</Label>
                <Input
                  type="number"
                  min="1"
                  max="500"
                  value={batchData.count}
                  onChange={(e) => setBatchData({ ...batchData, count: parseInt(e.target.value) || 10 })}
                  className="input-premium mt-2"
                />
              </div>
            </div>
            <div>
              <Label className="form-label">Batch Label</Label>
              <Input
                value={batchData.batch_label}
                onChange={(e) => setBatchData({ ...batchData, batch_label: e.target.value })}
                placeholder="Wedding Giveaway"
                className="input-premium mt-2"
                required
              />
              <p className="text-wedding-ivory/40 text-xs mt-1">
                Items will be labeled: {batchData.batch_label || "Label"} #1, #2, etc.
              </p>
            </div>
            <div>
              <Label className="form-label">Destination Type</Label>
              <Select value={batchData.destination_type} onValueChange={(v) => setBatchData({ ...batchData, destination_type: v })}>
                <SelectTrigger className="w-full bg-wedding-subtle border-white/10 mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-wedding-charcoal border-white/10">
                  {destinationTypes.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" onClick={() => setBatchModalOpen(false)} className="btn-secondary">
                Cancel
              </Button>
              <Button type="submit" className="btn-primary">
                Create {batchData.count} Artifacts
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR Code Modal */}
      <Dialog open={qrModalOpen} onOpenChange={setQrModalOpen}>
        <DialogContent className="bg-wedding-charcoal border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-wedding-ivory">QR Code</DialogTitle>
          </DialogHeader>
          {selectedArtifact && (
            <div className="text-center">
              <div className="qr-code-container mx-auto rounded-sm">
                <QRCodeSVG
                  value={getTokenUrl(selectedArtifact.token)}
                  size={200}
                  level="H"
                />
              </div>
              <p className="text-wedding-ivory/80 mt-4 font-mono text-sm">
                {selectedArtifact.token}
              </p>
              <p className="text-wedding-ivory/50 text-xs mt-2">
                {selectedArtifact.label}
              </p>
              <Button
                onClick={() => {
                  const svg = document.querySelector(".qr-code-container svg");
                  const svgData = new XMLSerializer().serializeToString(svg);
                  const canvas = document.createElement("canvas");
                  const ctx = canvas.getContext("2d");
                  const img = new Image();
                  img.onload = () => {
                    canvas.width = img.width;
                    canvas.height = img.height;
                    ctx.fillStyle = "white";
                    ctx.fillRect(0, 0, canvas.width, canvas.height);
                    ctx.drawImage(img, 0, 0);
                    const a = document.createElement("a");
                    a.download = `qr-${selectedArtifact.token}.png`;
                    a.href = canvas.toDataURL("image/png");
                    a.click();
                  };
                  img.src = "data:image/svg+xml;base64," + btoa(svgData);
                }}
                className="btn-secondary mt-4"
              >
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
